import { gql, useMutation } from "@apollo/client";
import axios from "axios";
import { ENV } from "../utils/configs";
import { client } from "../apolloClient";

const UPLOAD_FILE_TO_MINIO = gql`
  mutation UploadFileToMinio(
    $fileBase64: String!
    $filename: String!
    $schemaJsonBase64: String!
    $schemaFilename: String!
    $userId: Int!
  ) {
    uploadFileToMinio(
      fileBase64: $fileBase64
      filename: $filename
      schemaJsonBase64: $schemaJsonBase64
      schemaFilename: $schemaFilename
      user_id: $userId
    ) {
      tableName
      status
    }
  }
`;

const UPLOAD_FILE_TO_DB = gql`
  mutation UploadFileToDb(
    $tablename: String!
    $objectName: String!
    $schemaJson: SchemaJsonInput!
    $append: Boolean
    $sheetname: String
  ) {
    UploadFileToDb(
      tablename: $tablename
      objectName: $objectName
      schema_json: $schemaJson
      append: $append
      sheetname: $sheetname
    ) {
      tableName
      status
    }
  }
`;

const GET_DOWNLOAD_FILE_URL = gql`
  query downloadFileUrl($filePath: String!) {
    DownloadFileUrl(filePath: $filePath) {
      presigned_url
      object_name
    }
  }
`;

export const useUploadFile = () => {
  const [
    minioMutation,
    { loading: minioLoading, error: minioError, data: minioData },
  ] = useMutation(UPLOAD_FILE_TO_MINIO);
  const [dbMutation, { loading: dbLoading, error: dbError, data: dbData }] =
    useMutation(UPLOAD_FILE_TO_DB);

  const uploadFileToMinio = async (
    fileBase64,
    filename,
    schemaJsonBase64,
    schemaFilename,
    userId
  ) => {
    try {
      const { data } = await minioMutation({
        variables: {
          fileBase64,
          filename,
          schemaJsonBase64,
          schemaFilename,
          userId: parseInt(userId),
        },
      });
      if (!data?.uploadFileToMinio) {
        throw new Error("Upload failed");
      }
      return {
        success: true,
        data: data.uploadFileToMinio,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred during upload",
      };
    }
  };

  // Separate function for file upload via /upload API
  const uploadFile = async (file) => {
    try {
      const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const chunk = file.slice(start, end);
        const formData = new FormData();
        formData.append("chunk", chunk);
        formData.append("chunkIndex", i);
        formData.append("totalChunks", totalChunks);
        formData.append("uploadId", uploadId);
        formData.append("filename", file.name);
        try {
          await axios.post(`${ENV.service_url}/upload-chunk`, formData);
        } catch (chunkErr) {
          console.error(`Chunk ${i} upload error:`, chunkErr);
          throw new Error(`Chunk ${i} upload failed: ${chunkErr.message}`);
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      const hostName = window.location.hostname
     
      // Notify backend to assemble chunks
      const uploadResponse = await axios.post(`${ENV.service_url}/upload-chunk-complete`, {
        uploadId,
        filename: file.name,
        totalChunks,
        hostName
      });
      if (!uploadResponse.data || (!uploadResponse.data.presigned_url && !uploadResponse.data.object_name)) {
        throw new Error(uploadResponse.data?.message || "File upload failed");
      }

      // Backend returns presigned_url and object_name on success
      return {
        success: true,
        data: uploadResponse.data,
      };
    } catch (error) {
      console.error("File upload error:", error);
      return {
        success: false,
        error: error.message || "An error occurred during file upload",
      };
    }
  };

  // Separate function for UploadFileToDb mutation
  const uploadFileToDb = async (
    tableName,
    objectName,
    schemaJson,
    append,
    selectedSheet
  ) => {
    try {
      const { data } = await dbMutation({
        variables: {
          tablename: tableName,
          objectName: objectName,
          schemaJson: schemaJson,
          append: append,
          sheetname: selectedSheet,
        },
      });

      if (!data?.UploadFileToDb) {
        throw new Error("Database upload failed");
      }

      return {
        success: true,
        data: data.UploadFileToDb,
      };
    } catch (error) {
      console.error("Database upload error:", error);

      // Extract GraphQL error message
      let errorMessage = "An error occurred during database upload";

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        // GraphQL errors from the server
        errorMessage = error.graphQLErrors[0].message;
      } else if (error.networkError && error.networkError.result && error.networkError.result.errors) {
        // Network errors with GraphQL errors
        errorMessage = error.networkError.result.errors[0].message;
      } else if (error.message) {
        // Generic error message
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
        errors: error.graphQLErrors || [], // Include full GraphQL errors for detailed handling
      };
    }
  };

  const downloadFileUrl = async (filePath) => {
    try {
      const { data } = await client.query({
        query: GET_DOWNLOAD_FILE_URL,
        variables: {
          filePath,
        },
        fetchPolicy: "network-only",
      });

      if (!data?.DownloadFileUrl) {
        throw new Error("Failed to fetch download URL");
      }

      return {
        success: true,
        data: data.DownloadFileUrl,
      };
    } catch (error) {
      console.error("Download file URL error:", error);
      return {
        success: false,
        error: error.message || "An error occurred while fetching download URL",
      };
    }
  };

  return {
    uploadFileToMinio,
    uploadFile,
    uploadFileToDb,
    loading: minioLoading || dbLoading,
    minioLoading,
    dbLoading,

    // Error states
    error: minioError || dbError,
    minioError,
    dbError,

    // Data
    data: minioData || dbData,
    minioData,
    dbData,
    downloadFileUrl
  };
};
